import { DonationEligibilityInformation } from "src/types/toteTypes";

export enum ClassificationType {
  SDS = "SDS",
  AI_RESEARCH = "AI_RESEARCH",
}

export interface CapturedImage {
  file: File;
  displayUrl: string;
}

export interface ProductImage {
  url: string;
}

export interface WasteProfile {
  id: string;
}

export interface DonationCategory {
  id: string;
  name: string;
}

export interface SdsDocument {
  language?: string;
  url: string;
}

export interface Measurement {
  unit: string;
  value: number;
}

export interface Dimensions {
  height?: Measurement;
  length?: Measurement;
  width?: Measurement;
}

export interface HazmatStatement {
  value: string;
}

export interface ProductAttributes {
  weight?: Measurement;
  dimensions?: Dimensions;
  flash_point?: Measurement;
  light_type?: string;
  hazmat_state?: string;
  unid?: string[];
  hazmat_statement?: HazmatStatement[];
  ph_value?: number;
  product_group?: string;
}

export interface ProductClassification {
  asin: string;
  name?: string;
  marketplace_id?: number;
  images?: ProductImage[];
  waste_profiles: WasteProfile[];
  waste_profile_source?: string;
  waste_category?: string;
  waste_category_type?: string;
  is_hazardous?: boolean;
  transportation_classification?: string;
  hazmat_type?: string;
  hazmat_exceptions?: string[];
  profile_rationale?: string;
  eligible_for_donation?: boolean;
  donation_eligibility_information?: DonationEligibilityInformation;
  donation_categories?: DonationCategory[];
  sds_list?: SdsDocument[];
  product_attributes?: ProductAttributes;
  processing_attribute?: ItemProcessingAttribute;
}

/** Status of a waste classification session */
export enum WasteClassificationStatus {
  ANALYZING = "ANALYZING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

/** Image recognition output from the AI pipeline */
export interface ImageRecognitionResponse {
  product_name: string;
  matched_catalogue_id?: string;
  image_features: string[];
  inferred_ingredients: string[];
}

/** Waste profile returned by the AI classification */
export interface AIWasteProfile {
  profile_code: string;
}

/** AI-generated insight about the product */
export interface AIInsight {
  summary: string;
  visual_detection: string;
  ingredient_inference: string;
}

/** Waste classification output from the AI pipeline */
export interface WasteClassificationResponse {
  product_name: string;
  waste_profile: AIWasteProfile;
  ai_insight: AIInsight;
}

/** Combined science result from image recognition + waste classification */
export interface AIResearchResult {
  image_recognition: ImageRecognitionResponse;
  waste_classification: WasteClassificationResponse;
}

/** Response from POST /classification/ai-research */
export interface AIResearchResponse {
  classification_id: string;
  site_code: string;
  status: WasteClassificationStatus;
  captured_image_urls: string[];
  result: AIResearchResult;
}

export interface ItemDimension {
  width?: Measurement;
  length?: Measurement;
  height?: Measurement;
}

export interface Profile {
  value: string;
  description?: string;
}

export interface ImageVariant {
  label: string;
  url: string;
}

export interface ImageDimension {
  label: string;
  height: number;
  width: number;
}

export interface GetItemInformationRequest {
  id: string;
  locale: string;
  siteName: string;
  imageDimensions?: ImageDimension[];
}

export interface ItemProductAttribute {
  productName?: string;
  weight?: Measurement;
  dimensions?: ItemDimension;
  flashPoint?: Measurement;
  lightType?: string;
  hazmatStateOfMatter?: string;
  unid?: Profile[];
  hazmatStatement?: Profile[];
  phValue?: number;
  productGroup?: string;
}

export interface ItemComplianceAttribute {
  wasteCategory?: string;
  wasteCategoryType?: string;
  wasteProfile?: Profile[];
  wasteProfileSource?: string;
  isHazardous?: boolean;
  transportationClassification?: string;
  hazmatType?: string;
  hazmatExceptions?: Profile[];
  donationCategories?: DonationCategory[];
  profileRationale?: string;
}

export interface SafetyDataSheet {
  languages?: string[];
  url: string;
}

export interface SafetyDataSheetAttribute {
  englishSdsList?: SafetyDataSheet[];
}

export interface ItemInformation {
  asin: string;
  marketplaceId?: number;
  locale?: string;
  eligibleForDonation?: boolean;
  donationEligibilityInformation?: DonationEligibilityInformation;
  productAttribute?: ItemProductAttribute;
  complianceAttribute?: ItemComplianceAttribute;
  productImages?: ImageVariant[][];
  safetyDataSheetAttribute?: SafetyDataSheetAttribute;
  processingAttribute?: ItemProcessingAttribute;
}

export interface ItemProcessingAttribute {
  isVRET?: boolean;
  vretStatus?: string;
}

export interface GetItemInformationResponse {
  itemsInformationList: ItemInformation[];
}

export interface ClassifyWasteProfileRequest {
  itemName: string;
  asin: string;
  siteName: string;
}

export enum WasteProfilePathway {
  GOLDEN_ASIN = "GOLDEN_ASIN",
  SIMILARITY = "SIMILARITY",
  SDS_EXTRACTION = "SDS_EXTRACTION",
  AI_EXTERNAL_RESEARCH = "AI_EXTERNAL_RESEARCH",
}

export interface ClassifyWasteProfileResponse {
  recommendedWasteProfilesIds: string[];
  allWasteProfilesIds: string[];
  pathway: WasteProfilePathway;
  reasoning?: string;
}
